import { storageImage, storageSrcSet } from "@/lib/imageUrl";
import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Calendar, MessageCircle, Video, Building2, Package, TrendingUp, Users, GraduationCap, FileText, Film, Mic, Plane, ArrowRight, Sparkles, Share2, Phone, Mail } from "lucide-react";
import { Link } from "@/lib/router-compat";
import { BookingDialog } from "@/components/BookingDialogLazy";
import { SEOHead } from "@/components/SEOHead";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Process from "@/components/Process";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { useAdvancedScrollAnimation } from "@/hooks/useAdvancedScrollAnimation";
import { ParallaxLayer } from "@/components/animations/ParallaxSection";
import { Card3D } from "@/components/animations/Card3D";
import { MagneticButton } from "@/components/animations/MagneticButton";
import { WordByWordReveal } from "@/components/animations/WordByWordReveal";
import { AnimatedGradientText } from "@/components/animations/AnimatedGradientText";
import { FloatingParticles } from "@/components/animations/FloatingParticles";
import { ScrollIndicator } from "@/components/animations/ScrollIndicator";
import { trackWhatsAppClick, trackPhoneClick, trackEmailClick } from "@/lib/analytics";
import { useProjects } from "@/hooks/useProjects";
import { LazyCoverImage } from "@/components/LazyCoverImage";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Skeleton } from "@/components/ui/skeleton";
import Autoplay from "embla-carousel-autoplay";
import feedbackAudio from "@/assets/feedback-lee.mp3";
import { MarketFilter } from "@/components/MarketFilter";
import { RegionalFAQSection } from "@/components/seo/RegionalFAQSection";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { VideographyServicesGrid } from "@/components/VideographyServicesGrid";
import { useHeroImage } from "@/hooks/useHeroImages";
import ProcessTimeline from "@/components/ProcessTimeline";
import VideoMarketingBenefits from "@/components/VideoMarketingBenefits";
import PricingTransparency from "@/components/PricingTransparency";
import { Badge } from "@/components/ui/badge";
import GoogleRating from "@/components/GoogleRating";
import { BreadcrumbSchema, ServiceSchema } from "@/components/StructuredData";

const Videography = () => {
  const [showBooking, setShowBooking] = useState(false);
  const heroAnim = useAdvancedScrollAnimation({ variant: 'fadeUp', duration: 1000 });
  const portfolioAnim = useAdvancedScrollAnimation({ variant: 'blur', threshold: 0.2 });
  const servicesAnim = useAdvancedScrollAnimation({ variant: 'fadeUp', threshold: 0.1 });
  const whyUsAnim = useAdvancedScrollAnimation({ variant: 'slideLeft', threshold: 0.15 });
  
  const { data: projects = [], isLoading: projectsLoading } = useProjects({ publicOnly: true });
  const { data: heroImageData } = useHeroImage("videography");
  const [selectedMarket, setSelectedMarket] = useState<'de' | 'en'>('de');
  const videographyProjects = projects.filter(p => p.category === "videography" && p.language === selectedMarket);
  
  // Fallback to portfolio image if no hero image set
  const heroProject = videographyProjects[0];
  const heroImageUrl = heroImageData?.image_url;

  const whatsappHref = `https://wa.me/4917670027200?text=${encodeURIComponent("Hallo Clicks DMP, ich interessiere mich für Videoproduktion Services!")}`;
  const phoneHref = "tel:+4917670027200";
  const emailHref = "mailto:info@clicksdmp.com?subject=Anfrage%20Videoproduktion%20Services";

  const handleWhatsApp = () => {
    trackWhatsAppClick('videography_page');
  };

  const handlePhone = () => {
    trackPhoneClick();
  };

  const handleEmail = () => {
    trackEmailClick();
  };

  const videographyServices = [
    {
      title: "Imagefilm / Unternehmensfilm",
      subtitle: "Ihre Geschichte, emotional erzählt",
      description: "Ein guter Imagefilm ist mehr als bewegte Bilder – er ist das Herz Ihrer Marke. Wir erzählen die Geschichte und Expertise Ihres Unternehmens auf emotionale und authentische Weise, die Vertrauen schafft und Kunden überzeugt.",
      icon: Building2,
      keywords: ["imagefilm", "unternehmensfilm", "corporate", "brand"]
    },
    {
      title: "Industriefilm",
      subtitle: "Innovation sichtbar machen",
      description: "Produktionsprozesse, modernste Maschinen und technologische Innovationen verdienen eine professionelle Inszenierung. Wir zeigen die Kraft Ihrer Industrie direkt aus dem Herzen Ihres Unternehmens – beeindruckend und verständlich.",
      icon: Building2,
      keywords: ["industrie", "produktion", "technologie", "innovation"]
    },
    {
      title: "Produktfilm",
      subtitle: "Produkte, die verkaufen",
      description: "Zeigen Sie Ihre Produkte in Aktion! Unsere Produktfilme präsentieren Features, Vorteile und Anwendungsfälle so überzeugend, dass Kunden nicht widerstehen können. Perfekt für E-Commerce, Messen und Social Media.",
      icon: Package,
      keywords: ["produkt", "product", "showcase", "demo"]
    },
    {
      title: "Werbefilm / Commercial",
      subtitle: "Werbung, die fesselt",
      description: "Hochwertige, cinematische Werbefilme, die im Gedächtnis bleiben. Ob für TV, Kino oder Online-Kampagnen – wir produzieren Commercials, die Emotionen wecken und Ihre Botschaft kraftvoll transportieren.",
      icon: Film,
      keywords: ["werbefilm", "commercial", "werbung", "kampagne"]
    },
    {
      title: "Promotionsfilm",
      subtitle: "Aufmerksamkeit, die zählt",
      description: "Aktionen, Angebote oder Produkteinführungen brauchen maximale Aufmerksamkeit. Unsere Promotionsfilme sind dynamisch, prägnant und sorgen dafür, dass Ihre Botschaft ankommt – genau zur richtigen Zeit.",
      icon: TrendingUp,
      keywords: ["promotion", "promo", "aktion", "angebot"]
    },
    {
      title: "Recruitingfilm / Employer Branding",
      subtitle: "Talente begeistern, nicht nur informieren",
      description: "Die besten Talente wollen wissen, wofür sie arbeiten. Unsere Recruitingfilme zeigen Ihre Unternehmenskultur authentisch und emotional – so sprechen Sie genau die Mitarbeiter:innen an, die zu Ihnen passen.",
      icon: Users,
      keywords: ["recruiting", "employer", "branding", "karriere", "team"]
    },
    {
      title: "Schulungs- & E-Learning-Videos",
      subtitle: "Wissen, das hängen bleibt",
      description: "Komplexe Prozesse, Sicherheitsthemen oder Produktschulungen – wir vermitteln Wissen verständlich und anschaulich. Ihre Mitarbeiter:innen und Kund:innen lernen effizienter mit professionellen Videos.",
      icon: GraduationCap,
      keywords: ["schulung", "elearning", "training", "education"]
    },
    {
      title: "Erklärfilm (ohne Animation)",
      subtitle: "Klarheit durch echte Bilder",
      description: "Komplexe Produkte oder Dienstleistungen einfach erklärt – mit realen Szenen, klarer Sprache und verständlichen Bildern. Ihre Kund:innen verstehen auf Anhieb, was Sie anbieten und warum es für sie relevant ist.",
      icon: FileText,
      keywords: ["erklärfilm", "explainer", "erklärvideo", "tutorial"]
    },
    {
      title: "Dokumentation / Reportage",
      subtitle: "Echte Geschichten, echt erzählt",
      description: "Menschen, Prozesse und Geschichten hinter Ihrem Unternehmen verdienen eine professionelle Dokumentation. Wir fangen authentische Momente ein und erzählen Ihre Story so, wie sie wirklich ist.",
      icon: Film,
      keywords: ["dokumentation", "reportage", "documentary", "story", "restaurant", "harvest"]
    },
    {
      title: "Eventfilm / Messefilm",
      subtitle: "Events, die nachklingen",
      description: "Die Energie, Highlights und Emotionen Ihrer Veranstaltung festgehalten – professionell und atmosphärisch. Perfekt für Nachberichterstattung, Social Media oder als Teaser für kommende Events.",
      icon: Film,
      keywords: ["event", "messe", "veranstaltung", "kongress"]
    },
    {
      title: "Interview- & Testimonialfilm",
      subtitle: "Glaubwürdigkeit durch echte Stimmen",
      description: "Nichts überzeugt mehr als authentische Stimmen. Ob Mitarbeiter:innen, Kund:innen oder Führungskräfte – wir fangen echte Emotionen und ehrliche Meinungen ein, die Vertrauen und Nähe schaffen.",
      icon: Mic,
      keywords: ["interview", "testimonial", "statement", "kunde"]
    },
    {
      title: "Drohnenaufnahmen",
      subtitle: "Perspektiven, die beeindrucken",
      description: "Atemberaubende Luftaufnahmen für Außenansichten, Architektur, Unternehmensgelände und Events. Unsere Drohnenaufnahmen setzen Ihr Unternehmen perfekt in Szene – aus einer Perspektive, die begeistert.",
      icon: Plane,
      keywords: ["drohne", "drone", "aerial", "luftaufnahme"]
    }
  ];

  const whyChooseUs = [
    {
      title: "Cinematische Qualität",
      description: "Wir produzieren nicht einfach Videos – wir erschaffen cinematische Erlebnisse, die im Gedächtnis bleiben und Emotionen wecken."
    },
    {
      title: "Storytelling-Expertise",
      description: "Jeder Film erzählt eine Geschichte. Wir wissen, wie man Geschichten so erzählt, dass sie berühren, überzeugen und wirken."
    },
    {
      title: "Volle Produktion",
      description: "Von Konzept über Produktion bis Postproduktion – bei uns bekommen Sie alles aus einer Hand. Keine Schnittstellen, keine Missverständnisse."
    },
    {
      title: "Schnelle Umsetzung",
      description: "Zeit ist Geld. Wir arbeiten effizient, ohne Kompromisse bei der Qualität. Ihre Videos – pünktlich und perfekt."
    }
  ];

  return (
    <>
      <SEOHead
        title="Professionelle Videoproduktion | Imagefilme, Werbung & mehr | Clicks DMP"
        description="Hochwertige Videoproduktion für Unternehmen: Imagefilme, Werbefilme, Produktvideos, Recruiting & Events. Von Konzept bis Postproduktion – alles aus einer Hand."
        canonical="https://clicksdmp.com/film"
        ogImage="https://buhajnrjzecedelnxllv.supabase.co/storage/v1/object/public/project-media/covers/1760106893964-jehm010q68.jpg"
      />
      
      <BreadcrumbSchema items={[{ name: "Startseite", url: "https://clicksdmp.com/" }, { name: "Videoproduktion", url: "https://clicksdmp.com/film" }]} />
      <ServiceSchema serviceType="Videoproduktion" name="Videoproduktion in Mannheim und der Rhein-Neckar-Region" path="/film" description="Hochwertige Videoproduktion für Unternehmen: Imagefilme, Werbefilme, Produktvideos, Recruiting-Videos und Eventfilme, von der Idee bis zum fertigen Schnitt." />

      <div className="min-h-screen bg-background overflow-x-hidden">
        <Header />
        
        {/* Hero Section with Apple-Style Parallax */}
        <section 
          className="relative min-h-screen flex items-center justify-center overflow-hidden bg-background"
          style={{
            animation: 'page-entry 1.5s ease-out forwards',
            opacity: 0
          }}
        >
          {/* Layer 1: Background Image (slowest parallax) */}
          <ParallaxLayer speed={0.3} className="absolute inset-0">
            {heroImageUrl ? (
              <img
                src={storageImage(heroImageUrl, 1600, { quality: 78 })}
                srcSet={storageSrcSet(heroImageUrl, [800, 1200, 1600], { quality: 78 })}
                sizes="100vw"
                fetchPriority="high"
                alt="Videography Hero"
                className="w-full h-full object-cover"
                loading="eager"
              />
            ) : heroProject && (
              <LazyCoverImage
                projectId={heroProject.id}
                category={heroProject.category}
                title={heroProject.title}
                className="w-full h-full object-cover"
              />
            )}
          </ParallaxLayer>

          {/* Layer 2: Gradient Overlay */}
          <ParallaxLayer speed={0.5} className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/80" />
            <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-b from-transparent to-background" />
          </ParallaxLayer>

          {/* Layer 3: Floating Particles */}
          <ParallaxLayer speed={0.7} className="absolute inset-0">
            <FloatingParticles count={6} />
          </ParallaxLayer>

          {/* Layer 4: Text Content */}
          <ParallaxLayer speed={0.8} className="absolute inset-0 flex items-center justify-center pointer-events-none z-20">
            <div className="container-custom text-center max-w-5xl">
              <div className="flex flex-col items-center gap-6">
                {/* Animated Headline */}
                <WordByWordReveal
                  text="Videoproduktion in Mannheim und Rhein-Neckar"
                  className="text-4xl md:text-6xl lg:text-7xl font-bold text-white"
                  startDelay={1000}
                  wordDelay={100}
                />
                <WordByWordReveal
                  as="p"
                  text="Videoproduktion Rhein-Neckar"
                  className="text-4xl md:text-6xl lg:text-7xl font-bold text-white"
                  startDelay={1400}
                  wordDelay={100}
                />
                
                {/* Animated Gradient Subtitle */}
                <AnimatedGradientText
                  className="text-2xl md:text-4xl lg:text-5xl font-bold"
                  delay={1800}
                >
                  Geschichten die überzeugen
                </AnimatedGradientText>
                
                {/* Description */}
                <p 
                  className="text-base md:text-lg text-gray-300 max-w-3xl"
                  style={{
                    animation: 'fade-in 0.8s ease-out forwards',
                    animationDelay: '2.3s',
                    opacity: 0
                  }}
                >
                  Von Mannheim bis Frankfurt: Cinematische Videos für Unternehmen, 
                  die Emotionen wecken und Ihre Botschaft kraftvoll transportieren
                </p>

                {/* CTA Buttons */}
                <div 
                  className="flex flex-col sm:flex-row gap-4 mt-4 pointer-events-auto"
                  style={{
                    animation: 'fade-in 0.8s ease-out forwards',
                    animationDelay: '2.5s',
                    opacity: 0
                  }}
                >
                  <MagneticButton
                    onClick={() => setShowBooking(true)}
                    className="border-primary/30 bg-primary/5 text-primary backdrop-blur-xs hover:bg-primary/10 px-8 py-4 text-lg rounded-full font-semibold transition-all border flex items-center justify-center gap-2"
                    strength={0.3}
                  >
                    <Calendar className="w-5 h-5" />
                    Kostenloses Beratungsgespräch
                  </MagneticButton>
                  <MagneticButton
                    href={emailHref}
                    onClick={handleEmail}
                    className="border-primary/30 bg-primary/5 text-primary backdrop-blur-xs hover:bg-primary/10 px-8 py-4 text-lg rounded-full font-semibold transition-all border flex items-center justify-center gap-2"
                    strength={0.3}
                  >
                    <Mail className="w-5 h-5" />
                    E-Mail Kontakt
                  </MagneticButton>
                </div>
              </div>
            </div>
          </ParallaxLayer>

          {/* Scroll Indicator */}
          <ScrollIndicator targetId="regional-services" delay={2500} />
        </section>

        {/* Regional Service Areas Section */}
        <section id="regional-services" className="section-padding bg-gradient-to-b from-background to-primary/5">
          <div className="container-custom">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Videoproduktion in der <span className="text-gradient">Rhein-Neckar-Region</span>
              </h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto mb-12">
                Mit Sitz in Mannheim sind wir Ihre professionelle Videoproduktion für die gesamte Region – von Heidelberg über Ludwigshafen und Karlsruhe bis Stuttgart, Frankfurt, Mainz und Wiesbaden. Wir produzieren cinematische Imagefilme, Werbefilme, Produktvideos und mehr direkt bei Ihnen vor Ort im Südwesten Deutschlands.
              </p>
            </div>

            <div className="text-center mb-16">
              <h3 className="text-3xl md:text-4xl font-bold mb-6">
                Warum Video <span className="text-gradient">unverzichtbar</span> ist
              </h3>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                In einer Welt voller Content sind es die bewegten Bilder, die wirklich hängen bleiben
              </p>
            </div>

            <div className="max-w-3xl mx-auto mb-16">
              <div className="space-y-8">
                <div className="relative pl-8 border-l-4 border-primary/30 hover:border-primary transition-colors">
                  <h3 className="text-2xl font-bold mb-3">Emotionen in Bewegung</h3>
                  <p className="text-lg text-muted-foreground leading-relaxed">
                    Video transportiert nicht nur Informationen – es weckt Gefühle. Ein authentischer Imagefilm 
                    zeigt in 90 Sekunden mehr über Ihre Unternehmenskultur als 10 Seiten Text je könnten.
                  </p>
                </div>

                <div className="relative pl-8 border-l-4 border-primary/30 hover:border-primary transition-colors">
                  <h3 className="text-2xl font-bold mb-3">Menschen kaufen von Menschen</h3>
                  <p className="text-lg text-muted-foreground leading-relaxed">
                    Zeigen Sie die Gesichter hinter Ihrer Marke. Ein Testimonial-Video mit echten Kund:innen 
                    schafft mehr Vertrauen als jede Werbeaussage. Authentizität schlägt Perfektion.
                  </p>
                </div>

                <div className="relative pl-8 border-l-4 border-primary/30 hover:border-primary transition-colors">
                  <h3 className="text-2xl font-bold mb-3">Komplexes einfach erklärt</h3>
                  <p className="text-lg text-muted-foreground leading-relaxed">
                    Technische Produkte, komplizierte Dienstleistungen, abstrakte Konzepte – Video macht 
                    das Unsichtbare sichtbar und das Komplizierte verständlich. In Bewegung und mit Sound.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Process Timeline - Von der Idee zum Impact */}
        <ProcessTimeline />

        {/* Services Section */}
        <section id="services" className="section-padding bg-gradient-to-b from-background to-primary/5">
          <div className="container-custom">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Unsere <span className="text-gradient">Leistungen</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Von Imagefilmen bis Eventdokumentation – wir decken alle Ihre Videoproduktions-Anforderungen ab
              </p>
            </div>

            <VideographyServicesGrid />

            <div className="flex flex-wrap gap-4 justify-center mt-12">
              <Button 
                size="lg" 
                className="group"
                onClick={() => setShowBooking(true)}
              >
                Jetzt anfragen
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button 
                asChild
                variant="outline" 
                size="lg"
              >
                <a href={phoneHref} onClick={handlePhone}>
                  <Phone className="w-4 h-4 mr-2" />
                  Anrufen
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section ref={whyUsAnim.ref} className={`section-padding transition-all duration-1000 ${whyUsAnim.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="container-custom">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Warum <span className="text-gradient glow-text">Clicks DMP</span>?
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Mehr als nur Videos – eine Partnerschaft für Ihre visuelle Kommunikation
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {whyChooseUs.map((reason, index) => (
                <div
                  key={index}
                  className="text-center p-8 rounded-2xl bg-gradient-to-b from-primary/5 to-transparent border border-primary/10 hover:border-primary/30 transition-all duration-300"
                >
                  <h3 className="text-xl font-bold mb-4">{reason.title}</h3>
                  <p className="text-muted-foreground">{reason.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Customer Feedback Audio - Moved after Why Us */}
        <section className="section-padding bg-gradient-to-b from-primary/5 to-background">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto">
              {/* Header */}
              <div className="text-center mb-12">
                <h2 className="text-4xl md:text-5xl font-bold mb-6">
                  Was unsere Kunden sagen
                </h2>
                <p className="text-xl text-muted-foreground">
                  Lassen Sie sich das Feedback selbst anhören
                </p>
              </div>

              {/* Testimonial Card */}
              <div className="relative">
                {/* Background Gradient */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent rounded-3xl blur-2xl" />
                
                {/* Main Card */}
                <div className="relative bg-card/80 backdrop-blur-xs border-2 border-primary/20 rounded-3xl p-8 md:p-12 shadow-2xl">
                  {/* Client Info */}
                  <div className="flex items-start gap-6 mb-8">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
                      LM
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold mb-2">Lee Maxwell</h3>
                      <p className="text-muted-foreground mb-4">
                        Kundin · Rauenberg Festival 2022
                      </p>
                      
                      {/* Project Reference */}
                      <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full border border-primary/20">
                        <Video className="w-4 h-4 text-primary" />
                        <span className="text-sm font-medium">Über das Projekt:</span>
                        <Link 
                          to="/portfolio/rauenberg-festival-2022-ein-abend-voller-energie"
                          className="text-sm font-semibold text-primary hover:underline"
                        >
                          Rauenberg Festival 2022 – Ein Abend voller Energie
                        </Link>
                      </div>
                    </div>
                  </div>

                  {/* Audio Player */}
                  <div className="bg-background/50 border border-border rounded-2xl p-6 mb-8">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                        <Mic className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-semibold">Audio-Feedback</p>
                        <p className="text-sm text-muted-foreground">Originale Kundenstimme</p>
                      </div>
                    </div>
                    <audio 
                      controls 
                      className="w-full"
                      preload="metadata"
                      src={feedbackAudio}
                    >
                      Ihr Browser unterstützt das Audio-Element nicht.
                    </audio>
                  </div>

                  {/* Project Link CTA */}
                  <div className="flex justify-center">
                    <Link to="/portfolio/rauenberg-festival-2022-ein-abend-voller-energie">
                      <Button variant="hero-outline" size="lg" className="group">
                        <Video className="w-5 h-5" />
                        Projekt ansehen
                        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Video Marketing Benefits */}
        <VideoMarketingBenefits />

        {/* Portfolio Section */}
        <section id="portfolio" ref={portfolioAnim.ref} className={`section-padding bg-gradient-to-b from-background to-primary/5 transition-all duration-1000 ${portfolioAnim.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="container-custom">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Unsere <span className="text-gradient glow-text">Video</span> Projekte
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
                Sehen Sie, wie wir Marken durch professionelle Videoproduktion zum Leben erwecken
              </p>
              
              <MarketFilter 
                selectedMarket={selectedMarket}
                onMarketChange={setSelectedMarket}
                projectCount={videographyProjects.length}
              />
            </div>

            {projectsLoading ? (
              <div className="w-full max-w-6xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="rounded-xl overflow-hidden">
                      <Skeleton className="aspect-[4/3] w-full" />
                      <div className="p-6 space-y-2">
                        <Skeleton className="h-6 w-3/4" />
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-2/3" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : videographyProjects.length > 0 ? (
              <Carousel
                opts={{
                  align: "start",
                  loop: true,
                  dragFree: false,
                  watchDrag: true,
                }}
                plugins={[
                  Autoplay({
                    delay: 2000,
                    stopOnInteraction: false,
                    stopOnMouseEnter: true,
                  })
                ]}
                className="w-full max-w-6xl mx-auto"
              >
                <CarouselContent className="-ml-4">
                  {videographyProjects.map((project) => (
                    <CarouselItem key={project.id} className="pl-4 md:basis-1/2 lg:basis-1/3">
                      <Link 
                        to={`/portfolio/${project.slug}`} 
                        className="group block"
                      >
                        <div className="relative overflow-hidden rounded-xl bg-card border border-border transition-all duration-500 hover:border-primary/50 hover:shadow-xl hover:shadow-primary/20 hover:-translate-y-2">
                          {/* Image */}
                          <div className="relative aspect-[4/3] overflow-hidden">
                            <LazyCoverImage
                              projectId={project.id}
                              category={project.category}
                              title={project.title}
                              className="w-full h-full"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent opacity-60 group-hover:opacity-80 transition-opacity" />
                            
                            {/* Category Badge */}
                            <div className="absolute top-4 right-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-background/80 backdrop-blur-xs border border-border">
                              <Video className="w-4 h-4 text-primary" />
                              <span className="text-xs capitalize">Videoproduktion</span>
                            </div>
                          </div>

                          {/* Content */}
                          <div className="p-6">
                            <h3 className="text-xl font-bold mb-2 group-hover:text-primary transition-colors line-clamp-1">
                              {project.title}
                            </h3>
                            <p className="text-muted-foreground text-sm line-clamp-2 mb-4">
                              {project.description}
                            </p>
                            
                            {project.client && (
                              <p className="text-xs text-muted-foreground mb-4">
                                Kunde: {project.client}
                              </p>
                            )}
                            
                            <div className="flex items-center gap-2 text-primary text-sm font-medium">
                              <span>Projekt ansehen</span>
                              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </div>
                          </div>
                        </div>
                      </Link>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious className="hidden md:flex" />
                <CarouselNext className="hidden md:flex" />
              </Carousel>
            ) : (
              <div className="text-center py-12">
                <p className="text-muted-foreground">
                  Wir arbeiten noch an unserer Website. Das Videoproduktion-Portfolio wird in Kürze verfügbar sein.
                </p>
              </div>
            )}

            {/* View All Portfolio Link */}
            {videographyProjects.length > 0 && (
              <div className="text-center mt-12">
                <Link to={`/portfolio?market=${selectedMarket}`}>
                  <Button variant="hero-outline" size="lg" className="group">
                    Alle Projekte ansehen
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </section>

        {/* Process Section */}
        <Process />

        <GoogleRating />

        {/* FAQ Section */}
        <RegionalFAQSection
          title="Häufig gestellte Fragen zur Videoproduktion"
          subtitle="Alles über professionelle Videoproduktion in Mannheim, Heidelberg, Stuttgart und der Rhein-Neckar-Region"
          faqs={[
            {
              question: "In welchen Städten produzieren Sie Videos?",
              answer: "Wir produzieren Videos in der gesamten Rhein-Neckar-Region und darüber hinaus. Unser Hauptsitz ist in Mannheim, aber wir sind regelmäßig in Heidelberg, Ludwigshafen, Karlsruhe, Stuttgart, Frankfurt, Mainz und Wiesbaden unterwegs. Für größere Projekte reisen wir auch deutschlandweit."
            },
            {
              question: "Was kostet ein professioneller Imagefilm?",
              answer: "Ein professioneller Imagefilm startet bei ca. 3.500€. Der finale Preis hängt von Faktoren wie Drehtagen, Locations, Schauspielern und Postproduktionsaufwand ab. Kontaktieren Sie uns für ein individuelles Angebot basierend auf Ihren spezifischen Anforderungen."
            },
            {
              question: "Wie lange dauert die Produktion eines Videos?",
              answer: "Ein typischer Imagefilm dauert von Konzept bis Auslieferung etwa 4-8 Wochen. Das umfasst Vorproduktion, Dreh und Postproduktion. Für eilige Projekte können wir den Prozess beschleunigen. Kleinere Videos wie Social Media Content können oft innerhalb von 1-2 Wochen fertiggestellt werden."
            },
            {
              question: "Welche Video-Formate produzieren Sie?",
              answer: "Wir produzieren alle Arten von Unternehmensvideos: Imagefilme, Werbefilme, Produktvideos, Recruitingfilme, Event-Dokumentationen, Schulungsvideos, Erklärfilme und Social Media Content. Alle Videos werden in höchster Qualität und für alle benötigten Plattformen optimiert geliefert."
            },
            {
              question: "Bieten Sie auch Drohnenaufnahmen an?",
              answer: "Ja. Wir fliegen mit EU-Kompetenznachweis (A1/A3) und Drohnen-Haftpflichtversicherung. Perfekt für Immobilien, Unternehmensgelände, Events oder beeindruckende Establishing Shots für Imagefilme."
            },
            {
              question: "Was ist im Video-Preis enthalten?",
              answer: "In unseren Preisen ist die komplette Produktion enthalten: Konzeptentwicklung, Storyboarding, Dreh mit professionellem Equipment, Schnitt, Color Grading, Sound Design und alle Nutzungsrechte. Sie erhalten das fertige Video in verschiedenen Formaten für Web, Social Media und TV."
            }
          ]}
        />

        {/* Final CTA */}
        <section className="section-padding bg-gradient-to-b from-background to-primary/10">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Bereit für <span className="text-gradient glow-text">cinematische Videos</span>?
              </h2>
              <p className="text-xl text-muted-foreground mb-12">
                Lassen Sie uns gemeinsam Ihre Geschichte erzählen. Buchen Sie jetzt ein kostenloses Beratungsgespräch und erfahren Sie, wie wir Ihre Marke durch kraftvolle Videos zum Leben erwecken.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Button variant="hero" size="xl" onClick={() => setShowBooking(true)}>
                  <Calendar className="w-5 h-5" />
                  Jetzt Termin buchen
                </Button>
                <Button asChild variant="hero-outline" size="xl">
                  <a href={whatsappHref} target="_blank" rel="noopener" onClick={handleWhatsApp}>
                    <MessageCircle className="w-5 h-5" />
                    WhatsApp Kontakt
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        <Footer />
        <BookingDialog open={showBooking} onOpenChange={setShowBooking} />
      </div>
    </>
  );
};

export default Videography;
